import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft, Users, Mail, CreditCard, Banknote, Loader2, Download, Ban, Undo2, RotateCcw, CalendarClock, Image as ImageIcon, BadgeCheck, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: "waitlist", label: "Waitlist", icon: Mail },
  { id: "members", label: "Members", icon: Users },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "shifts", label: "Shift Board", icon: CalendarClock },
  { id: "verifications", label: "Verifications", icon: BadgeCheck },
  { id: "claims", label: "Claims", icon: ShieldCheck },
  { id: "bans", label: "Ban List", icon: Ban },
];

const endpoints = { waitlist: "waitlist", members: "members", payments: "payments", bans: "members/banned", shifts: "shifts", verifications: "verifications", claims: "claims" };
const rowKeys = { waitlist: "entries", members: "members", payments: "transactions", bans: "banned", shifts: "shifts", verifications: "verifications", claims: "claims" };

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}


function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => fetch(`${API}/admin/reports`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : { reports: [] }))
    .then((d) => setReports(d.reports || []))
    .catch(() => {});

  useEffect(() => { load(); }, []);

  const resolve = async (r, action) => {
    setBusy(r.id);
    try {
      const res = await fetch(`${API}/admin/reports/${r.id}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success(action === "ban" ? "Report resolved — account banned." : "Report dismissed.");
      load();
    } catch {
      toast.error("Could not resolve report.");
    } finally {
      setBusy("");
    }
  };

  const pending = reports.filter((r) => r.status === "pending");
  if (pending.length === 0) return null;

  return (
    <div data-testid="reports-panel" className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
      <p className="font-display text-lg font-bold uppercase text-amber-400">Reports to review ({pending.length})</p>
      <ul className="mt-4 space-y-4">
        {pending.map((r) => (
          <li key={r.id} data-testid={`report-${r.id}`} className="rounded-lg border border-white/10 bg-[#0f141c] p-5">
            <p className="text-sm text-slate-200"><strong>{r.shift_venue}</strong> · {r.shift_date}</p>
            <p className="mt-1 font-mono-tag text-[9px] text-slate-500">
              Reported by {r.reporter_name || r.reporter_email}{r.subject_email ? ` · about ${r.subject_email}` : ""}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{r.reason}</p>
            <div className="mt-4 flex gap-3">
              <button
                data-testid={`report-ban-${r.id}`}
                onClick={() => resolve(r, "ban")}
                disabled={!!busy}
                className="rounded-md bg-red-500 px-4 py-2 font-display text-xs font-bold uppercase text-white transition-all hover:bg-red-400 disabled:opacity-50"
              >
                {busy === r.id ? "…" : "Ban account"}
              </button>
              <button
                data-testid={`report-dismiss-${r.id}`}
                onClick={() => resolve(r, "dismiss")}
                disabled={!!busy}
                className="rounded-md border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase text-slate-400 transition-all hover:text-slate-50 disabled:opacity-50"
              >
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function LaunchControl() {
  const [cfg, setCfg] = useState({ app_store_url: "", play_store_url: "", launch_date: "" });
  const [busy, setBusy] = useState("");
  const [autopilot, setAutopilot] = useState(false);
  const [verifyAutopilot, setVerifyAutopilot] = useState(false);

  useEffect(() => {
    fetch(`${API}/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setCfg({
        app_store_url: d.app_store_url || "",
        play_store_url: d.play_store_url || "",
        launch_date: d.launch_date || "",
      }))
      .catch(() => {});
    fetch(`${API}/admin/autopilot`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        setAutopilot(!!d.auto_approve_shifts);
        setVerifyAutopilot(!!d.auto_approve_verifications);
      })
      .catch(() => {});
  }, []);

  const toggleAutopilot = async () => {
    setBusy("autopilot");
    try {
      const res = await fetch(`${API}/admin/autopilot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ auto_approve_shifts: !autopilot }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed");
      setAutopilot(data.auto_approve_shifts);
      toast.success(data.auto_approve_shifts
        ? "Autopilot ON — clean venue shifts go live by themselves."
        : "Autopilot OFF — every shift waits for your approval.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const toggleVerifyAutopilot = async () => {
    setBusy("verify-autopilot");
    try {
      const res = await fetch(`${API}/admin/autopilot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ auto_approve_verifications: !verifyAutopilot }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed");
      setVerifyAutopilot(data.auto_approve_verifications);
      toast.success(data.auto_approve_verifications
        ? "Verification autopilot ON — clean SIA submissions verify instantly."
        : "Verification autopilot OFF — every licence waits for your review.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const res = await fetch(`${API}/admin/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(cfg),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed");
      toast.success("Launch settings saved — store buttons update instantly.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const [cardVer, setCardVer] = useState(0);
  const [cardCity, setCardCity] = useState("");
  const [cardCities, setCardCities] = useState([]);
  const [rehearsal, setRehearsal] = useState(null);

  useEffect(() => {
    fetch(`${API}/shifts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCardCities([...new Set((d?.shifts || []).map((s) => s.city))]))
      .catch(() => {});
  }, []);

  const sendDigest = async () => {
    setBusy("digest");
    try {
      const res = await fetch(`${API}/admin/digest`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Digest failed");
      toast.success(data.sent ? "Morning digest sent to your inbox." : `Not sent: ${data.reason}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const runRehearsal = async () => {
    setBusy("rehearsal");
    setRehearsal(null);
    try {
      const res = await fetch(`${API}/admin/launch/rehearsal`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Rehearsal failed");
      setRehearsal(data);
      if (data.all_ok) toast.success("Rehearsal passed — launch day is clean.");
      else toast.error("Rehearsal found issues — see the checklist.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const regenerateCard = async () => {
    setBusy("card");
    try {
      const res = await fetch(`${API}/admin/share-card/regenerate${cardCity ? `?city=${cardCity}` : ""}`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Generation failed");
      setCardVer(Date.now());
      toast.success(`Share card regenerated${cardCity ? ` for ${cardCity}` : ""} with today's countdown.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const announce = async (eve) => {
    setBusy(eve ? "remind" : "announce");
    try {
      const res = await fetch(`${API}/admin/launch/${eve ? "remind" : "announce"}`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Send failed");
      toast.success(`${eve ? "Launch-eve teaser" : "Launch email"} sent to ${data.sent} waitlist signup${data.sent === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="launch-control" className="mt-10 rounded-xl border border-amber-500/25 bg-[#0f141c] p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold uppercase text-slate-50">Launch control</h2>
          <p className="mt-1 text-xs text-slate-500">Paste store links when approved — download buttons and the countdown finale go live instantly. The "we're live" email auto-sends to the waitlist at launch, or fire it manually.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="launch-remind-btn"
            onClick={() => announce(true)}
            disabled={!!busy}
            className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 font-display text-sm font-bold uppercase text-amber-500 transition-all hover:bg-amber-500/20 disabled:opacity-50"
          >
            {busy === "remind" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Send eve teaser now
          </button>
          <button
            data-testid="launch-announce-btn"
            onClick={() => announce(false)}
            disabled={!!busy}
            className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2.5 font-display text-sm font-bold uppercase text-red-400 transition-all hover:bg-red-500/20 disabled:opacity-50"
          >
            {busy === "announce" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Send launch email now
          </button>
          <button
            data-testid="digest-btn"
            onClick={sendDigest}
            disabled={!!busy}
            className="flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2.5 font-display text-sm font-bold uppercase text-slate-300 transition-all hover:border-amber-500/40 hover:text-amber-500 disabled:opacity-50"
          >
            {busy === "digest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Send digest now
          </button>
          <button
            data-testid="rehearsal-btn"
            onClick={runRehearsal}
            disabled={!!busy}
            className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 font-display text-sm font-bold uppercase text-emerald-400 transition-all hover:bg-emerald-500/20 disabled:opacity-50"
          >
            {busy === "rehearsal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Run launch rehearsal
          </button>
        </div>
      </div>

      {rehearsal && (
        <div data-testid="rehearsal-results" className="mt-5 rounded-lg border border-white/10 bg-[#07090e] p-5">
          <p className={`font-mono-tag text-[10px] ${rehearsal.all_ok ? "text-emerald-400" : "text-red-400"}`}>
            {rehearsal.all_ok ? "All systems go for Sep 28" : "Issues found — fix before launch"}
          </p>
          <ul className="mt-3 space-y-2">
            {rehearsal.checks.map((c) => (
              <li key={c.name} data-testid={`rehearsal-${c.name.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-start gap-2.5 text-sm">
                <span className={`mt-0.5 font-mono text-xs ${c.warn ? "text-amber-500" : c.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {c.warn ? "!" : c.ok ? "✓" : "✗"}
                </span>
                <span className="text-slate-300">{c.name}</span>
                <span className="text-slate-600">— {c.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
        <input
          data-testid="cfg-appstore-input"
          value={cfg.app_store_url}
          onChange={(e) => setCfg({ ...cfg, app_store_url: e.target.value })}
          placeholder="App Store URL (https://…)"
          className="h-12 rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
        />
        <input
          data-testid="cfg-playstore-input"
          value={cfg.play_store_url}
          onChange={(e) => setCfg({ ...cfg, play_store_url: e.target.value })}
          placeholder="Google Play URL (https://…)"
          className="h-12 rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
        />
        <input
          data-testid="cfg-launchdate-input"
          value={cfg.launch_date}
          onChange={(e) => setCfg({ ...cfg, launch_date: e.target.value })}
          placeholder="Launch date (2026-09-28T00:00:00Z)"
          className="h-12 rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
        />
      </div>
      <button
        data-testid="cfg-save-btn"
        onClick={save}
        disabled={!!busy}
        className="mt-4 flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
      >
        {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Save launch settings
      </button>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/8 bg-[#0f141c] p-5">
        <div>
          <h3 className="font-display text-base font-bold uppercase text-slate-50">Shift autopilot</h3>
          <p className="mt-1 max-w-lg text-xs leading-relaxed text-slate-500">
            When on, venue shifts that pass the rules (£12–£60/hr, 1–50 spots, dated from launch day,
            not a duplicate) go live on the board by themselves and the waitlist is emailed instantly.
            Anything unusual still queues for your approval, and you're emailed either way.
          </p>
        </div>
        <button
          data-testid="autopilot-toggle"
          onClick={toggleAutopilot}
          disabled={!!busy}
          className={`flex items-center gap-2 rounded-md px-5 py-2.5 font-display text-sm font-bold uppercase transition-all disabled:opacity-50 ${
            autopilot
              ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25"
              : "border border-white/10 text-slate-400 hover:text-slate-50"
          }`}
        >
          {busy === "autopilot" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {autopilot ? "Autopilot ON" : "Autopilot OFF"}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/8 bg-[#0f141c] p-5">
        <div>
          <h3 className="font-display text-base font-bold uppercase text-slate-50">Verification autopilot</h3>
          <p className="mt-1 max-w-lg text-xs leading-relaxed text-slate-500">
            When on, guard SIA licence submissions that pass the checks (16-digit format, no duplicate
            on another account) are verified instantly and can claim shifts immediately. You get an
            email for every auto-approval and can still reject it afterwards. Anything unusual queues
            for your manual review.
          </p>
        </div>
        <button
          data-testid="verification-autopilot-toggle"
          onClick={toggleVerifyAutopilot}
          disabled={!!busy}
          className={`flex items-center gap-2 rounded-md px-5 py-2.5 font-display text-sm font-bold uppercase transition-all disabled:opacity-50 ${
            verifyAutopilot
              ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25"
              : "border border-white/10 text-slate-400 hover:text-slate-50"
          }`}
        >
          {busy === "verify-autopilot" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {verifyAutopilot ? "Autopilot ON" : "Autopilot OFF"}
        </button>
      </div>

      <div className="mt-7 border-t border-white/8 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold uppercase text-slate-50">Social share card</h3>
            <p className="mt-1 text-xs text-slate-500">AI-generated daily with the live countdown. This is also the image shown when the site link is shared on social media.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="card-city-select"
              value={cardCity}
              onChange={(e) => setCardCity(e.target.value)}
              className="h-11 rounded-md border border-white/10 bg-[#07090e] px-3 text-sm text-slate-50 focus:border-amber-500/60 focus:outline-none"
            >
              <option value="">Global card</option>
              {cardCities.map((c) => <option key={c} value={c.toLowerCase()}>{c}</option>)}
            </select>
            <button
              data-testid="card-regenerate-btn"
              onClick={regenerateCard}
              disabled={!!busy}
              className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 font-display text-sm font-bold uppercase text-amber-500 transition-all hover:bg-amber-500/20 disabled:opacity-50"
            >
              {busy === "card" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              Regenerate card
            </button>
          </div>
        </div>
        <a data-testid="share-card-download" href={`${API}/share/card.png?${cardCity ? `city=${cardCity}&` : ""}v=${cardVer}`} target="_blank" rel="noreferrer" className="mt-4 block">
          <img
            data-testid="share-card-preview"
            src={`${API}/share/card.png?${cardCity ? `city=${cardCity}&` : ""}v=${cardVer}`}
            alt="GuardGig28 launch countdown share card"
            className="w-full max-w-xl rounded-lg border border-white/10 transition-opacity hover:opacity-90"
          />
        </a>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("waitlist");
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState("");
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/");
    else if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  const loadOverview = () => {
    fetch(`${API}/admin/overview`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setOverview)
      .catch(() => {});
    fetch(`${API}/admin/alerts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAlerts(d?.alerts || []))
      .catch(() => {});
  };

  const resolveAlert = (a) => act(`resolve-${a.id}`, () =>
    fetch(`${API}/admin/alerts/${a.id}/resolve`, { method: "POST", credentials: "include" }),
    "Alert resolved.");

  const loadRows = (t) => {
    setLoading(true);
    fetch(`${API}/admin/${endpoints[t]}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setRows(d[rowKeys[t]] || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadOverview();
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadRows(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  const act = async (key, fn, successMsg) => {
    setActing(key);
    try {
      const res = await fn();
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Action failed");
      toast.success(successMsg);
      loadRows(tab);
      loadOverview();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActing("");
    }
  };

  const ban = (r) => act(`ban-${r.user_id}`, () =>
    fetch(`${API}/admin/members/${r.user_id}/ban`, { method: "POST", credentials: "include" }),
    `${r.name} banned and sessions revoked.`);

  const unban = (r) => act(`unban-${r.user_id}`, () =>
    fetch(`${API}/admin/members/${r.user_id}/unban`, { method: "POST", credentials: "include" }),
    `${r.name} reinstated.`);

  const refund = (r) => act(`refund-${r.session_id}`, () =>
    fetch(`${API}/admin/payments/${r.session_id}/refund`, { method: "POST", credentials: "include" }),
    "Refund issued.");

  const approveShift = (r) => act(`approve-${r.id}`, () =>
    fetch(`${API}/admin/shifts/${r.id}/approve`, { method: "POST", credentials: "include" }),
    `${r.venue} approved — live on the public board.`);

  const approveVerification = (r) => act(`vapprove-${r.id}`, () =>
    fetch(`${API}/admin/verifications/${r.id}/approve`, { method: "POST", credentials: "include" }),
    `${r.name} verified — can now claim shifts.`);

  const rejectVerification = (r) => act(`vreject-${r.id}`, () =>
    fetch(`${API}/admin/verifications/${r.id}/reject`, { method: "POST", credentials: "include" }),
    `${r.name} verification rejected.`);

  const venueRate = (r, stars) => act(`vrate-${r.id}`, () =>
    fetch(`${API}/admin/claims/${r.id}/rate`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stars }),
    }),
    `Venue rated ${r.guard?.name} ${stars}★.`);

  const approveAppeal = (r) => act(`aapprove-${r.appeal.id}`, () =>
    fetch(`${API}/admin/appeals/${r.appeal.id}/approve`, { method: "POST", credentials: "include" }),
    `Appeal approved — ${r.name} reinstated.`);

  const denyAppeal = (r) => act(`adeny-${r.appeal.id}`, () =>
    fetch(`${API}/admin/appeals/${r.appeal.id}/deny`, { method: "POST", credentials: "include" }),
    "Appeal denied.");

  const rejectShift = (r) => act(`reject-${r.id}`, () =>
    fetch(`${API}/admin/shifts/${r.id}/reject`, { method: "POST", credentials: "include" }),
    `${r.venue} rejected.`);

  if (!user || user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#07090e]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const stats = overview
    ? [
        { label: "Members", value: overview.members, icon: Users },
        { label: "Waitlist signups", value: overview.waitlist, icon: Mail },
        { label: "Revenue (paid)", value: `£${overview.revenue.toLocaleString()}`, icon: Banknote },
        { label: "Banned accounts", value: overview.banned, icon: Ban },
      ]
    : [];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#07090e] pb-24">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Back to site</span>
          </button>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md border border-amber-500/30">
              <img src="/icons/icon-192.png" alt="GuardGig28 logo" className="h-full w-full object-cover" />
            </span>
            <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          </div>
          <span className="rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 font-mono-tag text-[9px] text-red-400">Admin</span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pt-12 sm:px-6">
        {alerts.length > 0 && (
          <div data-testid="panic-alerts-banner" className="mb-8 animate-pulse rounded-xl border-2 border-red-500 bg-red-500/10 p-6">
            <p className="font-display text-lg font-black uppercase text-red-400">Active panic alerts</p>
            <ul className="mt-3 space-y-3">
              {alerts.map((a) => (
                <li key={a.id} data-testid={`alert-${a.id}`} className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="text-sm text-slate-200"><strong>{a.guard_name}</strong> at {a.venue} · {new Date(a.created_at).toLocaleTimeString()}</span>
                  <a data-testid={`alert-map-${a.id}`} href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer"
                    className="font-mono-tag text-[9px] text-red-400 underline underline-offset-4">Location</a>
                  <button data-testid={`alert-resolve-${a.id}`} onClick={() => resolveAlert(a)} disabled={!!acting}
                    className="rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">
                    {acting === `resolve-${a.id}` ? "…" : "Resolve"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono-tag text-[11px] text-amber-500">Command center</p>
            <h1 className="font-display mt-3 text-4xl font-black uppercase tracking-tight text-slate-50 sm:text-5xl">Admin Dashboard</h1>
          </div>
          <a
            data-testid="waitlist-export-btn"
            href={`${API}/admin/waitlist/export`}
            className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-5 py-3 font-display text-sm font-bold uppercase text-amber-500 transition-all hover:bg-amber-500 hover:text-[#07090e]"
          >
            <Download className="h-4 w-4" /> Export waitlist CSV
          </a>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`admin-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-xl border border-white/8 bg-[#0f141c] p-6"
            >
              <s.icon className="h-5 w-5 text-amber-500" strokeWidth={1.75} />
              <p className="font-display mt-4 text-3xl font-black text-slate-50">{s.value}</p>
              <p className="font-mono-tag mt-1 text-[9px] text-slate-500">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {overview && (
          <motion.div
            data-testid="admin-sales-strip"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-6 rounded-xl border border-amber-500/25 bg-[#0f141c] p-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 font-mono-tag text-[11px] uppercase text-amber-500">
                <Crown className="h-4 w-4" /> Founding 500 — recent sales
              </p>
              <span data-testid="admin-founding-count" className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-mono-tag text-[9px] text-amber-400">
                {overview.founding_claimed} / {overview.founding_cap} claimed
              </span>
            </div>
            {overview.recent_founding_sales?.length ? (
              <ul className="mt-4 divide-y divide-white/5">
                {overview.recent_founding_sales.map((s, i) => (
                  <li key={`${s.email}-${s.when}`} data-testid={`sale-row-${i}`} className="flex flex-wrap items-center gap-x-5 gap-y-1 py-2.5">
                    <span className="text-sm font-semibold text-slate-200">{s.name}</span>
                    <span className="font-mono-tag text-[10px] text-slate-500">{s.email}</span>
                    <span className="font-mono-tag text-[10px] uppercase text-slate-500">{s.provider}</span>
                    <span className="ml-auto font-display text-sm font-bold text-amber-400">£{s.amount.toLocaleString()}</span>
                    <span className="font-mono-tag text-[9px] text-slate-600">{timeAgo(s.when)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p data-testid="admin-sales-empty" className="mt-4 text-sm text-slate-500">No Founding Pass sales yet — the first one lands here (and in your inbox) instantly.</p>
            )}
          </motion.div>
        )}

        <LaunchControl />
        <ReportsPanel />

        <div className="mt-10 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all ${
                tab === t.id
                  ? t.id === "bans" ? "bg-red-500 text-white" : "bg-amber-500 text-[#07090e]"
                  : "border border-white/10 text-slate-400 hover:text-slate-50"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div data-testid="admin-table" className="mt-6 overflow-hidden rounded-xl border border-white/8 bg-[#0f141c]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
            </div>
          ) : rows.length === 0 ? (
            <p data-testid="admin-empty" className="py-16 text-center font-mono-tag text-[10px] text-slate-600">
              {tab === "bans" ? "No banned accounts — rule 01 holding strong" : "No records yet"}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-white/8">
                    {(tab === "waitlist" ? ["Email", "Role", "Phone", "Joined"] :
                      tab === "members" ? ["Name", "Email", "Role", "Status", "Reliability", "Action"] :
                      tab === "shifts" ? ["Venue", "Area", "Date", "Time", "Rate", "Type", "Spots", "Status", "Action"] :
                      tab === "verifications" ? ["Guard", "License #", "Region", "Submitted", "Status", "Action"] :
                      tab === "claims" ? ["Guard", "Venue", "Status", "GPS", "Payout", "Venue rating"] :
                      tab === "bans" ? ["Name", "Email", "Banned at", "Action"] :
                      ["Reference", "Provider", "Amount", "Status", "Action"]
                    ).map((h) => (
                      <th key={h} className="px-5 py-4 font-mono-tag text-[9px] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tab === "waitlist" && rows.map((r) => (
                    <tr key={r.id} data-testid={`waitlist-row-${r.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.email}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${r.role === "guard" ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-400"}`}>{r.role}</span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-400">{r.phone || "—"}</td>
                      <td className="px-5 py-3.5 text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {tab === "members" && rows.map((r) => (
                    <tr key={r.user_id} data-testid={`member-row-${r.user_id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.name}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.email}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${r.role === "admin" ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}`}>{r.role}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${r.status === "banned" ? "bg-red-500/10 text-red-400" : "bg-white/5 text-slate-400"}`}>
                          {r.status === "banned" ? "Banned" : "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {r.reliability && (
                          r.reliability.score !== null ? (
                            <span data-testid={`reliability-${r.user_id}`} className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${
                              r.reliability.grade === "A" ? "bg-amber-500/10 text-amber-500" :
                              r.reliability.grade === "B" ? "bg-emerald-500/10 text-emerald-400" :
                              "bg-red-500/10 text-red-400"
                            }`} title={`${r.reliability.completed} completed · ${r.reliability.no_shows} no-show${r.reliability.no_shows === 1 ? "" : "s"}${r.reliability.rating_avg ? ` · ${r.reliability.rating_avg}★ venue avg` : ""}`}>
                              {r.reliability.grade} · {r.reliability.score}
                            </span>
                          ) : (
                            <span className="rounded-full bg-white/5 px-2.5 py-1 font-mono-tag text-[8px] text-slate-500">NEW</span>
                          )
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {r.role !== "admin" && (
                          r.status === "banned" ? (
                            <button data-testid={`unban-${r.user_id}`} onClick={() => unban(r)} disabled={!!acting}
                              className="flex items-center gap-1.5 rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
                              {acting === `unban-${r.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Reinstate
                            </button>
                          ) : (
                            <button data-testid={`ban-${r.user_id}`} onClick={() => ban(r)} disabled={!!acting}
                              className="flex items-center gap-1.5 rounded border border-red-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                              {acting === `ban-${r.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />} Ban
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {tab === "bans" && rows.map((r) => (
                    <tr key={r.user_id} data-testid={`banned-row-${r.user_id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.name}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.email}</td>
                      <td className="px-5 py-3.5 text-slate-500">
                        {r.banned_at ? new Date(r.banned_at).toLocaleString() : "—"}
                        {r.appeal && (
                          <span className={`mt-1 block font-mono-tag text-[8px] ${
                            r.appeal.status === "pending" ? "text-amber-500" :
                            r.appeal.status === "approved" ? "text-emerald-400" : "text-red-400"
                          }`} title={r.appeal.reason}>
                            appeal {r.appeal.status}: "{r.appeal.reason.slice(0, 60)}{r.appeal.reason.length > 60 ? "…" : ""}"
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-wrap gap-2">
                          {r.appeal?.status === "pending" ? (
                            <>
                              <button data-testid={`appeal-approve-${r.user_id}`} onClick={() => approveAppeal(r)} disabled={!!acting}
                                className="rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
                                {acting === `aapprove-${r.appeal.id}` ? "…" : "Approve appeal"}
                              </button>
                              <button data-testid={`appeal-deny-${r.user_id}`} onClick={() => denyAppeal(r)} disabled={!!acting}
                                className="rounded border border-red-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                                {acting === `adeny-${r.appeal.id}` ? "…" : "Deny"}
                              </button>
                            </>
                          ) : (
                            <button data-testid={`unban-list-${r.user_id}`} onClick={() => unban(r)} disabled={!!acting}
                              className="flex items-center gap-1.5 rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
                              {acting === `unban-${r.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Reinstate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tab === "shifts" && rows.map((r) => (
                    <tr key={r.id} data-testid={`shift-row-${r.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.venue}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.area}, {r.city}</td>
                      <td className="px-5 py-3.5 text-slate-300">{r.date}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.start}–{r.end}</td>
                      <td className="px-5 py-3.5 font-semibold text-amber-500">{r.currency === "GBP" ? "£" : "$"}{r.rate}/hr</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${r.type === "Armed" ? "bg-red-500/10 text-red-400" : r.type === "Supervisor" ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-400"}`}>{r.type}</span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-300">{r.spots}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${
                          r.status === "open" ? "bg-emerald-500/10 text-emerald-400" :
                          r.status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-red-500/10 text-red-400"
                        }`}>{r.status || "open"}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        {r.status === "pending" && (
                          <div className="flex gap-2">
                            <button data-testid={`approve-${r.id}`} onClick={() => approveShift(r)} disabled={!!acting}
                              className="rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
                              {acting === `approve-${r.id}` ? "…" : "Approve"}
                            </button>
                            <button data-testid={`reject-${r.id}`} onClick={() => rejectShift(r)} disabled={!!acting}
                              className="rounded border border-red-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                              {acting === `reject-${r.id}` ? "…" : "Reject"}
                            </button>
                          </div>
                        )}
                        {r.status === "open" && (
                          <span className="font-mono-tag text-[9px] text-emerald-400">Live</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {tab === "claims" && rows.map((r) => (
                    <tr key={r.id} data-testid={`claim-row-${r.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.guard?.name || "—"}
                        <span className="block text-xs text-slate-500">{r.guard?.email}</span>
                        {r.guard_score && (
                          r.guard_score.score !== null ? (
                            <span data-testid={`claim-score-${r.id}`} className={`mt-1 inline-block rounded-full px-2 py-0.5 font-mono-tag text-[8px] ${
                              r.guard_score.grade === "A" ? "bg-amber-500/10 text-amber-500" :
                              r.guard_score.grade === "B" ? "bg-emerald-500/10 text-emerald-400" :
                              "bg-red-500/10 text-red-400"
                            }`}>Reliability {r.guard_score.grade} · {r.guard_score.score}</span>
                          ) : (
                            <span className="mt-1 inline-block rounded-full bg-white/5 px-2 py-0.5 font-mono-tag text-[8px] text-slate-500">NEW GUARD</span>
                          )
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-300">{r.shift?.venue || "—"}<span className="block text-xs text-slate-500">{r.shift?.date}</span></td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${
                          r.status === "completed" ? "bg-sky-500/10 text-sky-400" :
                          r.status === "working" ? "bg-emerald-500/10 text-emerald-400" :
                          r.status === "no_show" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-500"
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-5 py-3.5 font-mono-tag text-[9px] text-slate-500">{r.checkin_verified ? "Verified" : "—"}</td>
                      <td className="px-5 py-3.5 text-slate-300">{r.payout_amount ? `$${r.payout_amount}` : "—"}</td>
                      <td className="px-5 py-3.5">
                        {r.status === "completed" && !r.venue_rated && (
                          <span className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button key={star} data-testid={`vrate-${r.id}-${star}`} onClick={() => venueRate(r, star)} disabled={!!acting}
                                className="text-slate-600 transition-all hover:scale-125 hover:text-amber-500 disabled:opacity-50">
                                ★
                              </button>
                            ))}
                          </span>
                        )}
                        {r.venue_rated && <span className="font-mono-tag text-[8px] text-emerald-400">Rated</span>}
                      </td>
                    </tr>
                  ))}
                  {tab === "verifications" && rows.map((r) => (
                    <tr key={r.id} data-testid={`verification-row-${r.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 text-slate-200">{r.name}<span className="block text-xs text-slate-500">{r.email}</span></td>
                      <td className="px-5 py-3.5 font-mono text-xs text-slate-300">{r.license_number}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.region}</td>
                      <td className="px-5 py-3.5 text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${
                          r.status === "approved" ? "bg-emerald-500/10 text-emerald-400" :
                          r.status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-red-500/10 text-red-400"
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        {r.status === "pending" && (
                          <div className="flex gap-2">
                            <button data-testid={`vapprove-${r.id}`} onClick={() => approveVerification(r)} disabled={!!acting}
                              className="rounded border border-emerald-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
                              {acting === `vapprove-${r.id}` ? "…" : "Approve"}
                            </button>
                            <button data-testid={`vreject-${r.id}`} onClick={() => rejectVerification(r)} disabled={!!acting}
                              className="rounded border border-red-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                              {acting === `vreject-${r.id}` ? "…" : "Reject"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {tab === "payments" && rows.map((r) => (
                    <tr key={r.session_id} data-testid={`payment-row-${r.session_id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                      <td className="max-w-[160px] truncate px-5 py-3.5 font-mono text-xs text-slate-400">{r.session_id}</td>
                      <td className="px-5 py-3.5 capitalize text-slate-300">{r.provider || "stripe"}</td>
                      <td className="px-5 py-3.5 text-slate-200">{r.amount} {String(r.currency || "").toUpperCase()}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${
                          r.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-400" :
                          r.payment_status === "refunded" ? "bg-slate-500/10 text-slate-400" :
                          r.payment_status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-red-500/10 text-red-400"
                        }`}>{r.payment_status}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        {r.payment_status === "paid" && (
                          <button data-testid={`refund-${r.session_id}`} onClick={() => refund(r)} disabled={!!acting}
                            className="flex items-center gap-1.5 rounded border border-amber-500/40 px-3 py-1.5 font-mono-tag text-[9px] text-amber-500 transition-colors hover:bg-amber-500/10 disabled:opacity-50">
                            {acting === `refund-${r.session_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
